<script setup>
import {computed, reactive, inject, ref, onMounted, watch, provide} from "vue";
//import functions
import {required} from "@vuelidate/validators";
import {discordRegistrationTime} from "@/utils/discordRegistrationTime.js";
import {formatPrice} from "@/utils/priceFormatter.js";
import {cleanObject} from "@/utils/cleanObject.js";
//import components
import {Icon} from "@iconify/vue";
import {
  Dialog,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  InputGroup,
  InputGroupAddon,
  InputText,
  FloatLabel,
  Select,
  Textarea,
  InputNumber,
  Tag
} from "primevue";
import Users from "@/components/Users.vue";
// import PublishHotOrder from "@/views/orders/Details/hotOrders/PublishHotOrder.vue";
//import stores
import {useUserStore} from "@/stores/user.js";
import {useCurrencyPricesStore} from "@/stores/currencyPrices.js";
import {useMainOrdersStore} from "@/stores/mainOrders.js";
import {useConfigStore} from "@/stores/config.js";
//import composables
import {useBreakpoint} from "@/composables/useBreakpoint.js";
import {useLoadingFlags} from "@/composables/useLoadingFlags.js";
import {useToast} from "primevue/usetoast";
import {useVuelidate} from "@vuelidate/core";
import {gregorianDate} from "@/utils/gregorianDate.js";
import {useRouter} from "vue-router";
import {showMessage} from "@/utils/showMessage.js";
import {useConfigData} from "@/composables/useConfigData.js";
import {useCustomerStore} from "@/stores/customer.js";

//stores
const customerStore = useCustomerStore();
const userStore = useUserStore();
const currencyPricesStore = useCurrencyPricesStore();
const ordersStore = useMainOrdersStore()
const configStore = useConfigStore();

//loading flags
const {
  saving,
  fetching,
  setSaving,
  setFetching,
} = useLoadingFlags()

//other composables
const {isXL} = useBreakpoint();
const toast = useToast();
const router = useRouter()

//emits
const emit = defineEmits(['refresh', 'selectCustomer']);

//props
const props = defineProps({
  selectedUser: {type: Object, required: true}
});

//injects
const visible = inject('isShowAddNewCustomerModal')

//component variables
const {stores} = useConfigData()
const customer = reactive({
  "store": 'Direct',
  "storeID": '',
  "name": '',
  "level": null
})
const rules = computed(() => {
  return {
    store: {required},
    storeID: {required}
  };
});
const $vuelidate = useVuelidate(rules, customer);

//component functions
const onSave = async () => {
  $vuelidate.value.$touch();
  if ($vuelidate.value.$invalid) {
    return false;
  }

  const payload = cleanObject(customer)

  try {
    saving.value = true
    const res = await customerStore.action_addNewCustomer(payload)
    if (res.result === true) {
      showMessage('New Customer added!', 'success')
      const customerID = res.data.id
      emit('selectCustomer', {
        id: customerID,
        ...customer
      })
      visible.value = false
    } else {
      showMessage(res, 'error')
    }
  } catch (err) {
    showMessage(err, 'error')
  } finally {
    saving.value = false
  }
}

//hooks

//provides
</script>

<template>
  <Dialog v-model:visible="visible"
          :class="['max-w-3xl !border-0 !rounded-md !bg-[#0e1726]', isXL ? '' : 'w-full']"
          :position="isXL ? 'center' : 'bottom'"
          closable dismissable-mask draggable modal
          content-class="min-w-full min-h-full flex flex-col gap-4 overflow-hidden h-full w-full max-w-[calc(100vw-20px)] max-h-[calc(100vh-100px)]">
    <template #header>
      <div class="text-base py-3">
        New Customer
      </div>
    </template>
    <template #closebutton="{closeCallback}">
      <div class="h-full w-[1px] bg-slate-700"></div>
      <button
          type="button"
          class="text-white-dark outline-none hover:text-red-500"
          @click="closeCallback"
      >
        <Icon icon="pajamas:close-xs"/>
      </button>
    </template>
    <div class="flex-1 flex flex-col gap-4 overflow-y-auto pt-4 pb-1 text-xs 2xl:text-sm min-w-80 max-w-full">
      <div class="flex-1 flex flex-col gap-0.5 text-xs 2xl:text-sm">
        <FloatLabel variant="on">
          <InputText id="storeID" v-model="customer.storeID" autocomplete="off"
                     class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                     :class="{ 'has-error': $vuelidate.storeID.$error }"/>
          <label for="storeID" class="!bg-slate-900"
                 :class="{ 'focus:text-red-300': $vuelidate.storeID.$error }">Store ID</label>
        </FloatLabel>
        <template v-if="$vuelidate.storeID.$error">
          <p class="text-red-300 mt-1 text-xs" v-if="!$vuelidate.storeID.required.$response">
            This field is required
          </p>
        </template>
      </div>
      <div class="flex flex-col gap-0.5 text-xs 2xl:text-sm">
        <FloatLabel class="w-full custom-select" :class="{ 'has-error': $vuelidate.store.$error }"
                    variant="on">
          <Select v-model="customer.store" inputId="store" :options="stores" optionLabel="store"
                  option-value="store"
                  class="w-full min-w-28 !bg-transparent h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm"
                  :class="[$vuelidate.store.$error ? '!border !border-red-300' : '!border-slate-700']"
                  input-class="!text-xs 2xl:!text-sm"
                  overlayClass="custom-select-overlay"
                  filter autoFilterFocus/>
          <label for="store" class="!bg-slate-900">Store</label>
        </FloatLabel>
        <template v-if="$vuelidate.store.$error">
          <p class="text-red-300 mt-1 text-xs" v-if="!$vuelidate.store.required.$response">
            This field is required
          </p>
        </template>
      </div>
      <div class="flex-1 flex flex-col gap-0.5 text-xs 2xl:text-sm">
        <FloatLabel variant="on">
          <InputText id="name" v-model="customer.name" autocomplete="off"
                     class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"/>
          <label for="name" class="!bg-slate-900">Name</label>
        </FloatLabel>
      </div>
      <div class="flex flex-col gap-0.5 text-xs 2xl:text-sm">
        <FloatLabel variant="on">
          <InputNumber v-model="customer.level" inputId="level" :maxFractionDigits="0" fluid autocomplete="off"
                       input-class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"/>
          <label for="level" class="!bg-slate-900">Level</label>
        </FloatLabel>
      </div>
    </div>
    <div class="flex justify-end gap-3">
      <button @click="onSave"
              class="btn btn-outline-dark flex items-center justify-center gap-3 disabled:opacity-50 py-1"
              :disabled="saving">
        Save
        <Icon v-if="saving" icon="eos-icons:bubble-loading" height="24"/>
      </button>
    </div>
  </Dialog>
</template>

<style></style>